import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { getSession, login as apiLogin, logout as apiLogout } from "../lib/api.ts";
import { clearPack, readPackMeta, verifyPackReady } from "../lib/offline.ts";
import { revokeThisDevice } from "../lib/push.ts";

const OFFLINE_READER = "tt-offline-reader";

type SessionState = {
  loading: boolean;
  authenticated: boolean;
  role: "family" | "owner" | null;
  offlineReader: boolean;
  sessionExpired: boolean;
  login: (password: string, displayName?: string) => Promise<void>;
  logout: () => Promise<void>;
  openDownloadedTrip: () => Promise<boolean>;
  markSessionExpired: () => void;
};

const SessionContext = createContext<SessionState | null>(null);

function hasOfflineReaderFlag() {
  try {
    return localStorage.getItem(OFFLINE_READER) === "1";
  } catch {
    return false;
  }
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const [loading, setLoading] = useState(true);
  const [authenticated, setAuthenticated] = useState(false);
  const [role, setRole] = useState<"family" | "owner" | null>(null);
  const [offlineReader, setOfflineReader] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);

  useEffect(() => {
    void getSession()
      .then(async (session) => {
        if (session.authenticated) {
          setAuthenticated(true);
          setRole(session.role ?? null);
          setOfflineReader(false);
          setSessionExpired(false);
          return;
        }
        if (hasOfflineReaderFlag()) {
          const ready = await verifyPackReady();
          if (ready.ok) {
            setAuthenticated(true);
            setRole("family");
            setOfflineReader(true);
          }
        }
      })
      .catch(async () => {
        if (hasOfflineReaderFlag() || readPackMeta().ready) {
          const ready = await verifyPackReady();
          if (ready.ok) {
            setAuthenticated(true);
            setRole("family");
            setOfflineReader(true);
          }
        }
      })
      .finally(() => setLoading(false));
  }, []);

  async function login(password: string, displayName?: string) {
    const result = await apiLogin(password, displayName);
    setAuthenticated(true);
    setRole(result.role);
    setOfflineReader(false);
    setSessionExpired(false);
  }

  async function openDownloadedTrip() {
    const ready = await verifyPackReady();
    if (!ready.ok) return false;
    try {
      localStorage.setItem(OFFLINE_READER, "1");
    } catch {
      /* quota */
    }
    setAuthenticated(true);
    setRole("family");
    setOfflineReader(true);
    return true;
  }

  async function logout() {
    try {
      await revokeThisDevice();
    } catch {
      /* still sign out */
    }
    try {
      await apiLogout();
    } catch {
      /* erase locally even if the network is gone */
    }
    await clearPack();
    try {
      localStorage.removeItem(OFFLINE_READER);
    } catch {
      /* ignore */
    }
    setAuthenticated(false);
    setRole(null);
    setOfflineReader(false);
    setSessionExpired(false);
  }

  return (
    <SessionContext.Provider
      value={{
        loading,
        authenticated,
        role,
        offlineReader,
        sessionExpired,
        login,
        logout,
        openDownloadedTrip,
        markSessionExpired: () => {
          setSessionExpired(true);
          setRole("family");
          setOfflineReader(true);
        },
      }}
    >
      {children}
    </SessionContext.Provider>
  );
}

export function useSession() {
  const value = useContext(SessionContext);
  if (!value) throw new Error("Session missing");
  return value;
}
